import { useState, useMemo } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useTestMode } from '@/hooks/useTestMode';
import { organizationDataAtom, campaignsDataAtom } from '@/store';
import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { navigation } from './constants';
import { OrganizationSelectionModal } from './organization-selection-modal';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { PerformanceBetaLink } from './performance-beta-link';
import { UserMenu } from '@/components/top-bar/user-menu';
import { OrgSwitcher } from '@/components/top-bar/org-switcher';
import { AdminMenu } from '@/components/top-bar/admin-menu';
import { useUserProfile } from '@/contexts/user.context';
import { businessQueryOptions } from '@/lib/query-options';
import { useStrategyReport } from '@/components/business/strategy-report-panel';
import { Spinner } from '@/components/glitch-ui/spinner';
import type { OrganizationDataWithMembership } from '@/lib/types';

interface NavigationItemProps {
  item: {
    name: string;
    to: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  location: {
    pathname: string;
  };
  showBadge?: boolean;
  // A small spinner at the item's end while something of its own is being
  // generated (Reports, while the strategy report runs).
  showSpinner?: boolean;
}

function NavigationItem({ item, location, showBadge, showSpinner }: NavigationItemProps) {
  // Active when the current path IS the item's route or a child of it, matched at
  // a path-segment boundary. A plain startsWith lit up /performance on both
  // /performance-v2 and /performance-consolidated (they share the prefix).
  const isActive =
    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  return (
    <SidebarMenuItem className='rounded-lg'>
      <SidebarMenuButton
        asChild
        className={`relative gap-3 text-[13px] py-5 px-4 font-normal font-sans rounded-lg outline-none border-none focus-visible:ring-2 focus-visible:ring-[#290BD9] focus-visible:ring-inset ${isActive ? 'text-[#290BD9] bg-[#290BD9]/10 !cursor-default' : 'text-gray-900 hover:bg-gray-50 !cursor-pointer'}`}
      >
        <Link to={item.to} aria-current={isActive ? 'page' : undefined}>
          <item.icon className='!h-5 !w-5' strokeWidth={1.5} />
          <span>{item.name}</span>
          {showBadge && (
            <span className="ml-auto h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0 group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:top-1 group-data-[collapsible=icon]:right-1.5 group-data-[collapsible=icon]:ml-0">!</span>
          )}
          {showSpinner && !showBadge && (
            <Spinner
              size="sm"
              className="ml-auto shrink-0 text-[#290BD9] group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:top-1 group-data-[collapsible=icon]:right-1.5 group-data-[collapsible=icon]:ml-0"
            />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function NavigationSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isAdminHidden, setIsAdminHidden] = useState(false);
  const { organizations, organizationData, switchOrganization } = useOrganizationInitialization();
  const testMode = useTestMode();
  const orgData = useAtomValue(organizationDataAtom);
  const campaigns = useAtomValue(campaignsDataAtom);
  const budgetNeedsAttention = testMode && !orgData?.max_spend && campaigns.some(c => (c.budget || 0) > 0);
  const { user } = useUserProfile();
  // Reports carries a spinner while the strategy report is being generated
  // (#6671): polled every 30s between runs so a run started elsewhere shows
  // too, and every 4s while one is running.
  const businessQuery = useQuery({ ...businessQueryOptions(orgData?.id ?? 0), enabled: !!orgData?.id });
  const { inFlight: strategyGenerating } = useStrategyReport(orgData?.id ?? 0, businessQuery.data?.id ?? 0, {
    idleIntervalMs: 30_000,
  });

  // A ``testModeOnly`` item shows in test mode; everything else always shows. The pre-GA
  // ``metaGated`` variant went away when Meta went live for everyone (#5976).
  const filteredNavigation = useMemo(() =>
    navigation.filter(item => {
      const gated = item as { testModeOnly?: boolean };
      if (gated.testModeOnly) return testMode;
      return true;
    }),
    [testMode]
  );

  const handleOrgModalClose = () => {
    setIsOrgModalOpen(false);
  };

  const handleOrganizationSelect = async (organization: OrganizationDataWithMembership) => {
    await switchOrganization(organization); // Secure switch with full cleanup
  };

  const isCollapsed = state === 'collapsed';

  return (
    <Sidebar className='bg-white border-r border-gray-200'>
      <SidebarContent className="flex flex-col">
        {/* Top: Org selector */}
        <div className={`border-b border-gray-200 ${isCollapsed ? 'flex justify-center px-1.5 py-[5px]' : 'py-1'}`}>
          <OrgSwitcher isCollapsed={isCollapsed} />
        </div>

        {/* Nav items */}
        <nav aria-label="Main" className="flex-1 flex flex-col">
          <SidebarMenu className="flex-1">
            {filteredNavigation.map((item) => (
              <NavigationItem
                key={item.name}
                item={item}
                location={location}
                showBadge={item.to === '/business' && budgetNeedsAttention}
                showSpinner={item.to === '/reports' && strategyGenerating !== null}
              />
            ))}
          </SidebarMenu>
        </nav>

        {/* Bottom, top to bottom: Admin (when shown), Performance beta, Account.
            Account sits last so it stays put whether or not Admin is shown.
            Notifications left this strip; the bell still lives in the top bar. */}
        <div className="border-t border-gray-200 px-1.5 py-2">
          <div className="flex flex-col gap-1">
            {user.role === 'ADMIN' && !isAdminHidden && <AdminMenu onHide={() => setIsAdminHidden(true)} />}
            <PerformanceBetaLink />
            <UserMenu />
          </div>
        </div>
      </SidebarContent>
      {organizations && (
        <OrganizationSelectionModal
          isOpen={isOrgModalOpen}
          onClose={handleOrgModalClose}
          organizations={organizations}
          selectedOrganization={organizationData || null}
          onOrganizationSelect={handleOrganizationSelect}
        />
      )}
    </Sidebar>
  );
}
